// Dashboard: usage & progress statistics (now category-based).
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Books, Target, Clock, FireSimple, ArrowRight, Lightning,
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
  PieChart, Pie, LineChart, Line, CartesianGrid,
} from 'recharts';
import { useStore, CATEGORY_LABEL } from '../lib/store';
import { Button } from '../components/ui/button';
import { useTheme } from '../contexts/ThemeContext';
import { DASH } from '../constants/testIds';

const TYPE_LABEL = {
  noun: 'İsim', adjective: 'Sıfat', adverb: 'Zarf/Edat', verb: 'Fiil',
};
const TYPE_COLORS = ['#002FA7', '#2A9D8F', '#7C3AED', '#E63946'];

const CATEGORY_COLORS = {
  learned: '#10B981', // emerald
  review:  '#38BDF8', // sky (açık mavi)
  unknown: '#EF4444', // rose  — "Zor"
  new:     '#F97316', // orange
};

export default function DashboardPage() {
  const { words, sessions } = useStore();
  const { theme } = useTheme();

  const cats = useMemo(() => {
    const c = { learned: 0, review: 0, unknown: 0, new: 0 };
    words.forEach((w) => { c[w.category || 'new'] += 1; });
    return c;
  }, [words]);

  const stats = useMemo(() => {
    const total = words.length;
    const totalKnown = words.reduce((s, w) => s + (w.knownCount || 0), 0);
    const totalUnknown = words.reduce((s, w) => s + (w.unknownCount || 0), 0);
    const totalAttempts = totalKnown + totalUnknown;
    const accuracy = totalAttempts ? Math.round((totalKnown / totalAttempts) * 100) : 0;
    return {
      total, totalKnown, totalUnknown, totalAttempts,
      accuracy,
      sessionCount: sessions.length,
    };
  }, [words, sessions]);

  const typeData = useMemo(() => {
    const m = { noun: 0, adjective: 0, adverb: 0, verb: 0 };
    words.forEach((w) => { if (m[w.type] !== undefined) m[w.type] += 1; });
    return Object.entries(m).map(([k, v]) => ({ name: TYPE_LABEL[k], value: v, key: k }));
  }, [words]);

  const categoryData = useMemo(() => ([
    { key: 'learned', name: 'Öğrendim',        value: cats.learned, color: CATEGORY_COLORS.learned },
    { key: 'review',  name: 'Kolay',           value: cats.review,  color: CATEGORY_COLORS.review },
    { key: 'new',     name: 'İlk Defa Gördüm', value: cats.new,     color: CATEGORY_COLORS.new },
    { key: 'unknown', name: 'Zor',             value: cats.unknown, color: CATEGORY_COLORS.unknown },
  ]), [cats]);

  const sessionData = useMemo(() => {
    const last = [...sessions].slice(0, 14).reverse();
    return last.map((s, i) => {
      const t = s.tally || {};
      const known = (t.learned || 0) + (t.review || 0);
      return {
        idx: i + 1,
        doğruluk: s.total ? Math.round((known / s.total) * 100) : 0,
      };
    });
  }, [sessions]);

  const topWords = useMemo(() => {
    return [...words]
      .map((w) => ({ ...w, attempts: (w.knownCount || 0) + (w.unknownCount || 0) }))
      .filter((w) => w.attempts > 0)
      .sort((a, b) => b.attempts - a.attempts)
      .slice(0, 6);
  }, [words]);

  // "En Zor 10 Kelime" — most-often-marked-as-Zor cards
  const hardestWords = useMemo(() => {
    return [...words]
      .filter((w) => (w.unknownCount || 0) > 0 || (w.category || 'new') === 'unknown')
      .sort((a, b) => {
        const ba = (b.unknownCount || 0) - (a.unknownCount || 0);
        if (ba !== 0) return ba;
        // Tie-breaker: category=unknown first, then more attempts
        const cat = (b.category === 'unknown' ? 1 : 0) - (a.category === 'unknown' ? 1 : 0);
        if (cat !== 0) return cat;
        return (b.knownCount + b.unknownCount) - (a.knownCount + a.unknownCount);
      })
      .slice(0, 10);
  }, [words]);

  const tickColor = theme === 'dark' ? '#A1A1AA' : '#5C5C5C';
  const lineColor = theme === 'dark' ? '#3366FF' : '#002FA7';
  const tooltipStyle = {
    background: theme === 'dark' ? '#121212' : '#fff',
    border: '1px solid hsl(var(--border))',
    borderRadius: 0,
    fontSize: 12,
  };

  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-12 py-12">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
        <div>
          <div className="overline mb-3">DASHBOARD · ÜBERSICHT</div>
          <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-none">
            İlerlemen.
          </h1>
        </div>
        <Button asChild className="rounded-none h-11">
          <Link to="/">Çalışmaya Başla <ArrowRight size={16} weight="bold" className="ml-1.5" /></Link>
        </Button>
      </div>

      {/* Bento metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 border border-border">
        <Metric testId={DASH.totalWords}   icon={Books}       label="Toplam Kelime" value={stats.total} />
        <Metric testId={DASH.accuracy}     icon={FireSimple}  label="Doğruluk"      value={`${stats.accuracy}%`} hint={`${stats.totalKnown}/${stats.totalAttempts}`} />
        <Metric testId={DASH.studiedSessions} icon={Clock}    label="Oturum"        value={stats.sessionCount} />
        <Metric testId={DASH.knownCount}   icon={Target}      label="Çalışılan"     value={words.filter((w) => (w.knownCount + w.unknownCount) > 0).length} />
      </div>

      {/* Category cards (the new spaced-repetition pillars) */}
      <div data-testid={DASH.categoryChart} className="grid grid-cols-2 lg:grid-cols-4 border border-t-0 border-border">
        <CategoryStat testId={DASH.learnedCount} color={CATEGORY_COLORS.learned} label="Öğrendim"        sub="ayda 1-2 tekrar" value={cats.learned} />
        <CategoryStat testId={DASH.reviewCount}  color={CATEGORY_COLORS.review}  label="Kolay"           sub="ayda 3-4 tekrar" value={cats.review} />
        <CategoryStat testId={DASH.newCount}     color={CATEGORY_COLORS.new}     label="İlk Defa Gördüm" sub="ayda 5-6 tekrar" value={cats.new} />
        <CategoryStat testId={DASH.unknownCount} color={CATEGORY_COLORS.unknown} label="Zor"             sub="ayda 7-8 tekrar" value={cats.unknown} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-t-0 border-border">
        {/* Category pie */}
        <div className="p-8 border-r border-border">
          <div className="overline mb-2">SPACED-REPETITION DAĞILIMI</div>
          <h3 className="font-display font-bold text-xl mb-6">Lernfortschritt</h3>
          {stats.total === 0 ? (
            <Empty msg="Henüz veri yok." />
          ) : (
            <div className="h-56">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={categoryData} dataKey="value" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {categoryData.map((entry) => (
                      <Cell key={entry.key} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Word type distribution */}
        <div data-testid={DASH.typeChart} className="p-8 border-r border-border">
          <div className="overline mb-2">KELİME TÜRÜ DAĞILIMI</div>
          <h3 className="font-display font-bold text-xl mb-6">Wortart-Verteilung</h3>
          {stats.total === 0 ? (
            <Empty msg="Henüz veri yok." />
          ) : (
            <div className="h-56">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={typeData} dataKey="value" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {typeData.map((entry, i) => (
                      <Cell key={entry.key} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          <ul className="mt-4 space-y-1.5 text-xs font-mono-plex">
            {typeData.map((d, i) => (
              <li key={d.key} className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5" style={{ background: TYPE_COLORS[i % TYPE_COLORS.length] }} />
                  {d.name}
                </span>
                <span className="text-muted-foreground">{d.value}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Session history */}
        <div data-testid={DASH.historyChart} className="p-8">
          <div className="overline mb-2">SON OTURUMLAR</div>
          <h3 className="font-display font-bold text-xl mb-6">Sitzungsverlauf</h3>
          {sessionData.length === 0 ? (
            <Empty msg="Henüz oturum tamamlamadın." />
          ) : (
            <div className="h-56">
              <ResponsiveContainer>
                <LineChart data={sessionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={tickColor} opacity={0.15} />
                  <XAxis dataKey="idx" stroke={tickColor} tick={{ fontSize: 11 }} />
                  <YAxis stroke={tickColor} tick={{ fontSize: 11 }} domain={[0, 100]} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line type="monotone" dataKey="doğruluk" stroke={lineColor} strokeWidth={2.5} dot={{ r: 3, fill: lineColor }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Hardest 10 words (repeatedly marked "Zor") */}
      <div className="grid grid-cols-1 border border-t-0 border-border">
        <div data-testid="dash-hardest-list" className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline mb-2" style={{ color: CATEGORY_COLORS.unknown }}>
                EN ZOR 10 KELİME
              </div>
              <h3 className="font-display font-bold text-xl">
                Schwierigste Vokabeln
              </h3>
            </div>
            {hardestWords.length > 0 && (
              <span className="overline text-muted-foreground">
                Toplam {hardestWords.reduce((s, w) => s + (w.unknownCount || 0), 0)} kez zor işaretlendi
              </span>
            )}
          </div>

          {hardestWords.length === 0 ? (
            <Empty msg="Henüz 'Zor' seçilmiş kelime yok." />
          ) : (
            <ol className="divide-y divide-border border-y border-border">
              {hardestWords.map((w, i) => (
                <li key={w.id} className="flex items-center gap-4 py-4">
                  <span className="font-display font-black text-3xl tracking-tighter text-muted-foreground w-10 tabular-nums">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span
                    className="w-1.5 h-10 flex-none"
                    style={{ background: CATEGORY_COLORS[w.category] || CATEGORY_COLORS.unknown }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-lg leading-tight truncate">
                      {w.type === 'noun' && w.article ? (
                        <span className="text-muted-foreground mr-1">{w.article}</span>
                      ) : null}
                      {w.german}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {w.turkish || <span className="italic">KI bekleniyor…</span>}
                    </div>
                  </div>
                  <div className="text-right flex-none">
                    <div className="font-mono-plex text-2xl font-bold text-rose-600 dark:text-rose-400 tabular-nums">
                      {w.unknownCount || 0}×
                    </div>
                    <div className="text-[10px] tracking-[0.2em] uppercase font-mono-plex text-muted-foreground">
                      zor
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {/* Top studied */}
      <div className="grid grid-cols-1 border border-t-0 border-border">
        <div className="p-8">
          <div className="overline mb-2">EN ÇOK ÇALIŞILANLAR</div>
          <h3 className="font-display font-bold text-xl mb-6">Meistgeübte Karten</h3>
          {topWords.length === 0 ? (
            <Empty msg="Bir oturum tamamlayınca burası dolacak." />
          ) : (
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={topWords} layout="vertical">
                  <XAxis type="number" stroke={tickColor} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="german" stroke={tickColor} tick={{ fontSize: 12 }} width={140} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="knownCount"   stackId="x" fill="#10B981" />
                  <Bar dataKey="unknownCount" stackId="x" fill="#EF4444" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Metric({ icon: Icon, label, value, hint, testId }) {
  return (
    <div data-testid={testId} className="p-8 border-r last:border-r-0 border-border">
      <div className="flex items-center justify-between mb-6">
        <div className="overline">{label}</div>
        <Icon size={18} weight="duotone" className="text-muted-foreground" />
      </div>
      <div className="font-display font-black text-5xl tracking-tighter leading-none mb-2">
        {value}
      </div>
      {hint && <div className="text-xs text-muted-foreground font-mono-plex">{hint}</div>}
    </div>
  );
}

function CategoryStat({ color, label, sub, value, testId }) {
  return (
    <div data-testid={testId} className="p-6 border-r last:border-r-0 border-border relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: color }} />
      <div className="flex items-center justify-between mb-4">
        <div className="overline" style={{ color }}>{label}</div>
        <Lightning size={16} weight="duotone" style={{ color }} />
      </div>
      <div className="font-display font-black text-4xl tracking-tighter leading-none">
        {value}
      </div>
      <div className="text-[10px] tracking-[0.2em] uppercase font-mono-plex text-muted-foreground mt-2">
        {sub}
      </div>
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="h-40 border border-dashed border-border flex items-center justify-center text-sm text-muted-foreground">
      {msg}
    </div>
  );
}
